import { useCallback, useRef, useState } from 'react';
import { AnySchema, ValidationError } from 'yup';
import { useStringsContext } from '../contexts/strings';

export type EditedValue = Record<string, any>;
export type ValidatedValue = Record<string, boolean>;

export const useFormValidate = <T extends EditedValue>() => {
  const editedValues = useRef<T>({} as T);
  const validatedValues = useRef<ValidatedValue>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const addFormValue = useCallback(
    (key: string, value: any) => {
      editedValues.current[key as keyof T] = value;
      setIsDirty(true);
    },
    [editedValues]
  );

  const addFormValidation = useCallback(
    (key: string, isValid: boolean) => {
      validatedValues.current[key] = isValid;
      setIsValid(Object.values(validatedValues.current).every((valid) => valid));
    },
    [validatedValues]
  );

  const removeFormValue = useCallback((key: string) => {
    delete editedValues.current[key];
    delete validatedValues.current[key];
  }, []);

  return {
    editedValues: editedValues.current,
    validatedValues: validatedValues.current,
    addFormValue,
    addFormValidation,
    removeFormValue,
    isValid,
    isDirty,
  };
};

export const useFieldValidation = () => {
  const { strings } = useStringsContext();
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const doValidate = useCallback(
    async (value: unknown, validationSchema: AnySchema) => {
      let isValid = true;
      let errorMessage: string | null = null;

      try {
        await validationSchema.validate(value);
      } catch (error: unknown) {
        isValid = false;
        errorMessage =
          error instanceof ValidationError ? error.message : strings.messages.validateError;
      }

      setIsValid(isValid);
      setErrorMessage(errorMessage);

      return isValid;
    },
    [strings]
  );

  return {
    doValidate,
    isValid,
    errorMessage,
  };
};
